import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  FormStep,
  ListingFormValues,
  listingFormSchema, // Import the main schema
} from "@/components/listings/filters/FilterSchema";
import { getFieldsForStep } from "../utils/formUtils";
import { z, ZodObject, ZodTypeAny } from "zod"; // Import z and necessary Zod types
import { Listing } from "@/lib/types"; // Import Listing type

// Helper to build a partial schema for current step's fields
const buildStepSchema = (
  fields: string[],
  baseSchema: ZodObject<any, any, any>
): ZodObject<any, any, any> => {
  const shape: Record<string, ZodTypeAny> = {};
  fields.forEach((field) => {
    const path = field.split(".");
    let currentSchemaNode = baseSchema.shape;
    let currentShapeNode = shape;

    path.forEach((part, index) => {
      if (!currentSchemaNode || !currentSchemaNode[part]) {
        console.warn(
          `[buildStepSchema] Field part '${part}' not found in base schema for field '${field}'.`
        );
        return; // Skip this field part if not found
      }
      if (index === path.length - 1) {
        // Check if the field is required in the base schema
        const isRequired = !(
          currentSchemaNode[part].isOptional() ||
          currentSchemaNode[part].isNullable()
        );
        currentShapeNode[part] = isRequired
          ? currentSchemaNode[part]
          : currentSchemaNode[part].optional();
      } else {
        if (
          !currentShapeNode[part] ||
          !(currentShapeNode[part] instanceof ZodObject)
        ) {
          currentShapeNode[part] = z.object({});
        }
        currentShapeNode = (currentShapeNode[part] as ZodObject<any, any, any>)
          .shape;
        if (currentSchemaNode[part] instanceof ZodObject) {
          currentSchemaNode = (
            currentSchemaNode[part] as ZodObject<any, any, any>
          ).shape;
        } else {
          // This case implies a path like 'a.b' where 'a' is not an object in the schema.
          console.warn(
            `[buildStepSchema] Schema path mismatch for '${field}' at '${part}'. Expected ZodObject.`
          );
          currentSchemaNode = {}; // Prevent further errors down this path
        }
      }
    });
  });
  return z.object(shape);
};

// Helper to extract values for the current step's fields
const getStepValues = (
  fields: string[],
  allValues: ListingFormValues
): Record<string, any> => {
  const stepValues: Record<string, any> = {};
  fields.forEach((field) => {
    const path = field.split(".");
    let currentValueNode: any = allValues;
    let currentStepValueNode = stepValues;

    path.forEach((part, index) => {
      if (
        currentValueNode === undefined ||
        currentValueNode === null ||
        typeof currentValueNode !== "object" ||
        !(part in currentValueNode)
      ) {
        currentValueNode = undefined; // Value not found or path broken
        return; // Stop traversing this path
      }
      currentValueNode = currentValueNode[part];

      if (index === path.length - 1) {
        currentStepValueNode[part] = currentValueNode;
      } else {
        if (
          !currentStepValueNode[part] ||
          typeof currentStepValueNode[part] !== "object"
        ) {
          currentStepValueNode[part] = {};
        }
        currentStepValueNode = currentStepValueNode[part];
      }
    });
  });
  return stepValues;
};

export const useFormValidation = (
  form: UseFormReturn<ListingFormValues>,
  currentStep: FormStep,
  // formTouched: boolean, // formTouched might still be useful for some UX aspects, but removed from deps for now
  validateOnChange: boolean,
  setCanProceedToNextStep: (value: boolean) => void,
  setStepCompleted: (step: FormStep, isCompleted: boolean) => void,
  draftListing: Listing | undefined,
  isFormInitializedWithFetchedData: boolean // Add new parameter
) => {
  // Watch the fields relevant to the current step to trigger re-validation
  const fieldsForCurrentStep = getFieldsForStep(currentStep);
  const watchedValues = form.watch(fieldsForCurrentStep as any); // RHF's watch can take an array of field names

  useEffect(() => {
    console.log(
      `[useFormValidation] useEffect running. Step: ${FormStep[currentStep]}, validateOnChange: ${validateOnChange}`
    );
    // The watchedValues are now implicitly part of the closure for validateCurrentStep
    // or could be passed if preferred, but re-running the effect due to watchedValues change is key.

    const validateCurrentStep = async () => {
      // Special handling for Property Details step (Step 1)
      if (currentStep === FormStep.PropertyDetails) {
        console.log(
          `[useFormValidation] Property Details Step (Step 1): Always enabling Next button.`
        );
        setCanProceedToNextStep(true);
        setStepCompleted(currentStep, true); // Mark as completed to allow navigation
        return; // Exit early as no further validation is needed for this step
      }

      // Special validation for Location step
      if (currentStep === FormStep.Location) {
        const cityId = form.getValues("location.city_id");
        const isValidLocationStep = typeof cityId === "number" && cityId > 0;

        console.log(
          `[useFormValidation] Location Step Validation: City ID: ${cityId}, Valid: ${isValidLocationStep}`
        );
        setCanProceedToNextStep(isValidLocationStep);
        if (isValidLocationStep) {
          setStepCompleted(currentStep, true);
        } else {
          setStepCompleted(currentStep, false);
        }
        return; // Exit early for Location step as its validation is custom
      }

      // Special validation for Media step
      if (currentStep === FormStep.Media) {
        const images = draftListing?.images || [];
        const hasEnoughImages = images.length >= 3;
        // The user wants to allow navigation if images are present,
        // even if a primary image hasn't been explicitly selected.
        // The MediaStep's handleUpload already sets the first image as primary by default.
        const isValidMediaStep = hasEnoughImages;

        console.log(
          `[useFormValidation] Media Step Validation: Images: ${images.length}, Valid: ${isValidMediaStep}`
        );
        setCanProceedToNextStep(isValidMediaStep);
        if (isValidMediaStep) {
          setStepCompleted(currentStep, true);
        } else {
          setStepCompleted(currentStep, false);
        }
        return; // Exit early for Media step as its validation is custom
      }

      // fieldsForCurrentStep is already defined outside from the watch
      if (
        currentStep === FormStep.Review &&
        fieldsForCurrentStep.length === 0
      ) {
        setCanProceedToNextStep(true);
        return;
      }

      if (validateOnChange) {
        // This is when user clicks "Next" and validation is forced for UI feedback
        console.log(
          `[useFormValidation] Actively validating step ${FormStep[currentStep]} due to validateOnChange.`
        );
        const isValid = await form.trigger(fieldsForCurrentStep as any, {
          shouldFocus: true, // Focus on first error when actively validating
        });
        console.log(
          `[useFormValidation] Active validation for step ${FormStep[currentStep]} result: ${isValid}. Errors:`,
          JSON.stringify(form.formState.errors)
        );
        setCanProceedToNextStep(isValid);
        if (isValid) {
          setStepCompleted(currentStep, true);
        }
      } else {
        // Initial silent check when step loads or validateOnChange is false
        // Initial silent check when step loads or validateOnChange is false
        // Only perform strict validation if validateOnChange is true (user interaction)
        // OR if the form has been initialized with fetched data.
        if (validateOnChange || isFormInitializedWithFetchedData) {
          if (fieldsForCurrentStep.length > 0) {
            const stepSchema = buildStepSchema(
              fieldsForCurrentStep,
              listingFormSchema
            );
            const allCurrentValues = form.getValues();
            const stepValuesToValidate = getStepValues(
              fieldsForCurrentStep,
              allCurrentValues
            );

            const result = stepSchema.safeParse(stepValuesToValidate);
            console.log(
              `[useFormValidation] Initial silent check for step ${FormStep[currentStep]}. Schema used:`,
              JSON.stringify(stepSchema.shape),
              `Values:`,
              JSON.stringify(stepValuesToValidate),
              `Valid: ${result.success}`
            );
            if (!result.success) {
              console.log(
                "[useFormValidation] Silent check failed. Errors:",
                JSON.stringify(result.error.flatten().fieldErrors)
              );
            }
            setCanProceedToNextStep(result.success);
          } else {
            setCanProceedToNextStep(true);
          }
        } else {
          // If not actively validating and form not yet initialized with fetched data,
          // allow progression to enable user to fill in details.
          console.log(
            `[useFormValidation] Skipping strict silent check for step ${FormStep[currentStep]} as form not yet initialized with fetched data. Allowing progression.`
          );
          setCanProceedToNextStep(true);
          setStepCompleted(currentStep, true); // Mark as completed to allow navigation
        }
      }
    };

    validateCurrentStep();
  }, [
    form, // form reference itself
    currentStep,
    validateOnChange,
    setCanProceedToNextStep,
    setStepCompleted,
    // JSON.stringify(watchedValues), // Adding watchedValues (or a stable representation) to dependencies
    // Note: Spreading an object/array directly into deps might cause too many re-runs if not careful.
    // A more stable approach is often JSON.stringify(watchedValues) or useMemo for a stable key.
    // For now, let's try with watchedValues directly, assuming RHF provides stable references or it's an array of primitives.
    // If watchedValues is an object, its reference might change on each render if not memoized by RHF's watch.
    // A safer bet is to stringify or map to a primitive array if fields are dynamic.
    // Given fieldsForCurrentStep can change, stringifying the watched object is safer.
    JSON.stringify(watchedValues),
    draftListing,
    isFormInitializedWithFetchedData, // Add to dependencies
  ]);
};
